import React, { useContext, useEffect, useState, useCallback } from "react";
import { AppContext } from "../utilities/context";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import Container from "@mui/material/Container";
import Layout from "./Layout";
import Grid from "@mui/material/Grid";
import { Button, Box, TextField, Paper, Typography, Alert, CircularProgress } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useDropzone } from "react-dropzone";
import DynamicJsonForm from "./DynamicJsonForm";

const Shop = () => {
  const intl = useIntl();
  const context = useContext(AppContext);
  const [shop, setShop] = useState(null);
  const [name, setName] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const getShop = () => {
    setLoading(true);
    axios.get("/api/shop/mine")
      .then((response) => {
        const data = response.data;
        setShop(data);
        setName(data.name || "");
        setAdditionalInfo(data.additionalInfo || {});
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    getShop();
    if (context) {
      context.updateBreadcrumbsState([
        { label: "Home", url: "/" },
        { label: "Tienda" },
      ]);
    }
  }, []);

  const handleJsonChange = (path, value) => {
    setAdditionalInfo(prev => {
      const copy = structuredClone(prev);
      const keys = path.split(".");
      let current = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    maxFiles: 1,
  });

  const handleSave = async () => {
    setSaving(true);
    setAlert(null);
    try {
      let payload;
      const headers = {};
      if (logoFile) {
        payload = new FormData();
        payload.append("name", name);
        payload.append("additionalInfo", JSON.stringify(additionalInfo));
        payload.append("logo", logoFile);
        headers["Content-Type"] = "multipart/form-data";
      } else {
        payload = { name, additionalInfo };
      }
      await axios.put("/api/shop/mine", payload, { headers });
      setLogoFile(null);
      setLogoPreview(null);
      getShop();
      setAlert({ severity: "success", message: "Guardado correctamente" });
    } catch (err) {
      setAlert({ severity: "error", message: err.message });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth={false} disableGutters>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth={false} disableGutters>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={10}>
            {alert && (
              <Alert severity={alert.severity} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
                {alert.message}
              </Alert>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Configuración de la tienda</Typography>

              <TextField
                label="Nombre de la tienda"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 3 }}
              />

              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Logo
              </Typography>

              <Box
                {...getRootProps()}
                sx={{
                  border: "1px dashed #ccc",
                  p: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  mb: 3,
                  borderRadius: 1,
                }}
              >
                <input {...getInputProps()} />
                {logoPreview ? (
                  <Box
                    component="img"
                    src={logoPreview}
                    sx={{ maxHeight: 100, mx: "auto" }}
                  />
                ) : shop?.logo ? (
                  <Box
                    component="img"
                    src={process.env.REACT_APP_API_URL + shop.logo}
                    sx={{ maxHeight: 100, mx: "auto" }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Arrastrá un logo aquí o hacé click para seleccionar
                  </Typography>
                )}
              </Box>

              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Información adicional (JSON)
              </Typography>

              <DynamicJsonForm
                data={additionalInfo}
                onChange={handleJsonChange}
              />

              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={1}></Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default Shop;
